/**
 * The composer (prototype composer()): a growing field, @mention,
 * #channel and :emoji suggestions (server emoji written `:name:`, sent as
 * `<:name:id>`: customEmoji.tsx), a reply bar, emoji, and image upload (images only
 * in the alpha). Images come from the picker, paste, or a drop anywhere on
 * the chat; any size is accepted: each one is optimized first (shrunk and
 * re-encoded in the image worker, metadata such as GPS dropped; see
 * lib/image), with its progress on its chip. Send works at once: a message
 * sent while its images are still optimizing waits for them in the channel
 * ("Optimizing… n%", then uploading). Enter sends, Shift+Enter breaks the
 * line. The console's limits apply as the live config gives them: images
 * are shrunk to fit its upload limit, and a message takes at most its
 * number of images.
 */
import {
  attachmentsPerMessage,
  ChannelType,
  type Channel,
  type Guild,
  type Message,
  type Role,
  type Snowflake,
} from '@neosocial/protocol';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { create } from 'zustand';
import { useT, type Translator } from '../i18n';
import {
  IMAGE_ACCEPT,
  imageErrorText,
  isAbort,
  looksLikeImage,
  optimizeImage,
  renameFor,
  stillText,
} from '../lib/image';
import { useConfig } from '../store/config';
import { useData } from '../store/data';
import {
  channelsInSidebarOrder,
  displayName,
  isDeletedUser,
  presenceOf,
  sortedRoles,
  userName,
} from '../store/selectors';
import { reloadHistory, sendMessage, sendTyping, type PreparedFiles } from '../store/messages';
import { readLayout, useLayout } from '@neosocial/ui/layout';
import { Avatar, Icon } from '../ui/kit';
import { openMenu, toast } from '../ui/overlay';
import { Emoji, openEmoji } from './emoji';
import { startEdit } from './Message';
import { PollCreator, useCanPoll } from './Poll';
import { customName, encodeCustomEmoji, isCustomToken } from './customEmoji';
import { EMOJI_QUERY_RE, suggestEmoji } from './emojiSuggest';
import { usableCustomEmoji, useCustomEmojiOn, useEmoji } from '../store/emoji';
// slowmode (2026-10-29): the bar and the countdown
import { SlowmodeBar } from './Slowmode';
import { countdownText, noteSlowmodeSend, useSlowmode } from '../store/slowmode';

/** The optimizing of one image, shared by its chip and a message sent before it is ready. */
export interface PickJob {
  /** The file to send; null when the image could not be used (already toasted) or was stopped. */
  done: Promise<File | null>;
  /** 0..1 */
  progress: number;
  listeners: Set<() => void>;
  ctl: AbortController;
}

/** An image on its way into a message: optimizing, then ready to send. */
export interface ImagePick {
  key: number;
  source: File;
  state: 'working' | 'ready';
  progress: number;
  out: File | null;
  ctl: AbortController;
  /** The same object in every copy of this pick (patches copy the rest). */
  job: PickJob;
}

let pickSeq = 0;

/** Hands images that may still be optimizing to a message, which waits for them. */
export function preparedFiles(jobs: PickJob[]): PreparedFiles {
  return {
    count: jobs.length,
    ready: Promise.all(jobs.map((j) => j.done)).then((fs) => fs.filter((f): f is File => !!f)),
    onProgress(cb) {
      const tick = () => cb(jobs.reduce((n, j) => n + j.progress, 0) / Math.max(1, jobs.length));
      for (const j of jobs) j.listeners.add(tick);
      tick();
    },
    abort() {
      for (const j of jobs) j.ctl.abort();
    },
  };
}

/** "1.2 MB" / "340 KB", in the UI's digits. */
export function sizeText(t: Translator['t'], bytes: number): string {
  if (bytes >= 1024 * 1024) return t('img.mb', { n: Math.round((bytes / 1024 / 1024) * 10) / 10 });
  return t('img.kb', { n: Math.max(1, Math.round(bytes / 1024)) });
}

/** The server's default limit, counted as it counts: characters (code points) of the text sent. */
export const MAX_CONTENT = 2000;

/** How long `text` is for the server's limit (code points, not UTF-16 units). */
export function contentLength(text: string): number {
  return [...text].length;
}

/** The longest message now: the console's `limits.message_length` (the server enforces it too). */
export function useMaxContent(): number {
  return useConfig((s) => s.config?.settings['limits.message_length']) ?? MAX_CONTENT;
}

interface Suggest {
  kind: 'user' | 'channel' | 'every' | 'role' | 'emoji';
  id: string;
  insert: string;
  label: string;
  sub: string;
  user?: Message['author'];
  /** An emoji suggestion's emoji (its token). */
  emoji?: string;
}

type MentionUser = { id: Snowflake; username: string };
type MentionRole = Pick<Role, 'id' | 'name'>;

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const END = '(?=$|[\\s,.!?:;])';

/** One pass over `text`, longest name first, so "@ali" never eats the start of "@ali.k". */
function replaceNames(text: string, sigil: string, names: Map<string, string>): string {
  if (!names.size) return text;
  const alts = [...names.keys()].sort((a, b) => b.length - a.length).map(escapeRe);
  const re = new RegExp(escapeRe(sigil) + '(' + alts.join('|') + ')' + END, 'g');
  return text.replace(re, (m, name: string) => names.get(name) ?? m);
}

/**
 * Turns typed @username, @Role and #channel into wire mentions. Only roles
 * passed here are encoded (the caller passes the ones you may mention).
 */
export function encodeMentions(
  text: string,
  users: MentionUser[],
  channels: Channel[],
  roles: MentionRole[] = [],
): string {
  const at = new Map<string, string>();
  for (const r of roles) if (r.name) at.set(r.name, `<@&${r.id}>`);
  // a username wins over a role of the same name (the person was picked by their handle)
  for (const u of users) at.set(u.username, `<@${u.id}>`);
  const hash = new Map<string, string>();
  for (const c of channels)
    if (c.name && c.type === ChannelType.GUILD_TEXT) hash.set(c.name, `<#${c.id}>`);
  return replaceNames(replaceNames(text, '@', at), '#', hash);
}

/**
 * The other way, for the edit box: wire mentions back to what you would
 * type (@username, @Role, #channel), so editing shows text, not IDs, and
 * encodeMentions() turns it back. Unknown IDs stay as they are.
 */
export function decodeMentions(
  content: string,
  users: MentionUser[],
  channels: Channel[],
  roles: MentionRole[] = [],
): string {
  const u = new Map(users.map((x) => [x.id, x.username]));
  const r = new Map(roles.map((x) => [x.id, x.name]));
  const c = new Map(
    channels.filter((x) => x.name && x.type === ChannelType.GUILD_TEXT).map((x) => [x.id, x.name!]),
  );
  return content
    .replace(/<@!?(\d+)>/g, (m, id: string) => (u.has(id) ? '@' + u.get(id) : m))
    .replace(/<@&(\d+)>/g, (m, id: string) => (r.has(id) ? '@' + r.get(id) : m))
    .replace(/<#(\d+)>/g, (m, id: string) => (c.has(id) ? '#' + c.get(id) : m));
}

/** Text on its way into a channel's draft (`insertIntoDraft`), by channel. */
const useDraftInserts = create<{ pending: Record<Snowflake, string> }>(() => ({ pending: {} }));

/**
 * Puts `text` into a channel's draft at the caret, a space on either side
 * where it needs one (a voice member menu's Mention, as Discord's): the
 * composer showing that channel takes it now, or as soon as it opens. Wire
 * mentions read as you would type them (`decodeMentions`): `<@id>` shows
 * as @username and goes out as `<@id>` again.
 */
export function insertIntoDraft(channelId: Snowflake, text: string): void {
  useDraftInserts.setState((s) => {
    const prev = s.pending[channelId];
    return { pending: { ...s.pending, [channelId]: prev ? `${prev} ${text}` : text } };
  });
}

/** What waits for `channelId`'s draft, taken (nothing waits for it after). */
function takeDraftInsert(channelId: Snowflake): string | undefined {
  const text = useDraftInserts.getState().pending[channelId];
  if (text === undefined) return undefined;
  useDraftInserts.setState((s) => {
    const pending = { ...s.pending };
    delete pending[channelId];
    return { pending };
  });
  return text;
}

/**
 * `add` into `draft` at `at`, with a space before it unless the draft is
 * empty there or already has one, and one after unless one follows.
 * Returns the new draft and the caret after what went in and its space.
 */
export function spliceDraft(
  draft: string,
  at: number,
  add: string,
): { text: string; caret: number } {
  const before = draft.slice(0, at);
  const after = draft.slice(at);
  const lead = before && !/\s$/.test(before) ? ' ' : '';
  const trail = /^\s/.test(after) ? '' : ' ';
  const head = before + lead + add + trail;
  // the caret after the space, the one it brought or the one that was there
  return { text: head + after, caret: head.length + (trail ? 0 : 1) };
}

/** The roles you may @mention in a guild: mentionable ones, or all with Mention Everyone (as the server checks). */
export function mentionableRoles(guild: Guild | undefined, canEveryone: boolean): Role[] {
  if (!guild) return [];
  return sortedRoles(guild).filter(
    (r) => r.id !== guild.id && !r.managed && (r.mentionable || canEveryone),
  );
}

export function Composer({
  channel,
  name,
  placeholder,
  replyTo,
  onCancelReply,
  canSend,
  canAttach,
  canEveryone,
  topBar,
  onSent,
}: {
  channel: Channel;
  name: string;
  placeholder: string;
  replyTo: Message | null;
  onCancelReply(): void;
  canSend: boolean;
  canAttach: boolean;
  canEveryone: boolean;
  /** A bar attached to the composer's top edge (the jump-to-present bar, §7.12). */
  topBar?: ReactNode;
  /** A message went out: the list scrolls to the end (I8). */
  onSent?(): void;
}) {
  const { t, fmt } = useT();
  // the console's limits.message_length (the server enforces it too)
  const maxLength = useMaxContent();
  const [text, setText] = useState('');
  const [picks, setPicks] = useState<ImagePick[]>([]);
  const [dropping, setDropping] = useState(false);
  const [sel, setSel] = useState(0);
  const [caret, setCaret] = useState(0);
  // Esc closed the suggestion list; typing opens it again
  const [dismissed, setDismissed] = useState(false);
  // a reply pings its author unless the reply bar's @ is turned off (Discord)
  const [ping, setPing] = useState(true);
  const ta = useRef<HTMLTextAreaElement>(null);
  const fileIn = useRef<HTMLInputElement>(null);
  // polls (2026-10-29): the "+" menu's Create poll, and its dialog
  const canPoll = useCanPoll(channel);
  const [polling, setPolling] = useState(false);
  // The console's limits (live config; the server enforces both).
  const uploadImageBytes = useConfig((s) => s.config?.settings['limits.upload_image_bytes']);
  const maxFiles = useConfig((s) =>
    attachmentsPerMessage(s.config?.settings['limits.attachments_per_message']),
  );
  const guildId = channel.guild_id;
  const members = useData((d) => (guildId ? d.members[guildId] : undefined));
  const channels = useData((d) => d.channels);
  const meId = useData((d) => d.me?.id);
  const guild = useData((d) => (guildId ? d.guilds[guildId] : undefined));
  const roles = useMemo(() => mentionableRoles(guild, canEveryone), [guild, canEveryone]);
  // server emoji for :name: suggestions (they follow the live list and the console's flag)
  const emojiLists = useEmoji((s) => s.byGuild);
  const customOn = useCustomEmojiOn();
  const replyName = useData((d) => (replyTo ? userName(d, replyTo.author.id, guildId) : ''));
  const replyMine = !!replyTo && replyTo.author.id === meId;
  const slow = useSlowmode(channel);

  const wrap = useRef<HTMLDivElement>(null);
  // a touch screen (§12.4 rule 3, §12.7 A3): opening a channel never raises the
  // keyboard by itself (the field takes the focus on the person's tap), and the
  // keyboard's Enter is a new line; the send button sends (Discord's mobile app)
  const lay = useLayout();
  const touch = lay.pointer === 'coarse' && lay.layout !== 'desktop';
  useEffect(() => {
    if (readLayout().pointer !== 'coarse') ta.current?.focus();
  }, [channel.id]);
  // a new reply pings again: set while rendering, from the reply it was set for
  const [pingFor, setPingFor] = useState(replyTo);
  if (pingFor !== replyTo) {
    setPingFor(replyTo);
    if (replyTo) setPing(true);
  }
  useEffect(() => {
    if (replyTo) ta.current?.focus();
  }, [replyTo]);

  /**
   * The field follows its text: 44 at rest, at most half the window (§7.11);
   * on a phone or tablet, half of what the on-screen keyboard leaves.
   */
  const grownFor = useRef({ length: 0, lines: 0 });
  const grow = () => {
    const el = ta.current;
    if (!el) return;
    const vv = (globalThis as { visualViewport?: VisualViewport }).visualViewport;
    const h = readLayout().layout !== 'desktop' && vv ? vv.height : window.innerHeight;
    const max = Math.round(h / 2) - 16;
    // Typing on (the text only got longer, no line went away) can only grow the
    // field: its scroll height says by how much, read without first collapsing
    // it to `auto`. The collapse relaid the whole chat column out twice per
    // keystroke (chat performance, 2026-11-01); it runs only when text went away.
    const lines = el.value.split('\n').length;
    const prev = grownFor.current;
    const longer = el.value.length >= prev.length && lines >= prev.lines;
    grownFor.current = { length: el.value.length, lines };
    if (longer && el.style.height && el.clientHeight <= max) {
      if (el.scrollHeight > el.clientHeight && el.clientHeight < max)
        el.style.height = Math.min(el.scrollHeight, max) + 'px';
      return;
    }
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, max) + 'px';
  };

  const people = useMemo(() => {
    if (guildId)
      return Object.values(members ?? {}).map((m) => ({
        user: m.user,
        name: displayName(m.user, m),
      }));
    // a deleted account can't be mentioned (its handle is a stand-in nobody types)
    return (channel.recipients ?? [])
      .filter((u) => !isDeletedUser(u))
      .map((u) => ({ user: u, name: displayName(u) }));
  }, [guildId, members, channel.recipients]);

  const suggest = useMemo((): {
    kind: string;
    start: number;
    items: Suggest[];
    q?: string;
  } | null => {
    const upto = text.slice(0, caret);
    let m = /(^|\s)@([^\s@]*)$/.exec(upto);
    if (m) {
      const q = (m[2] ?? '').toLowerCase();
      const state = useData.getState();
      const items: Suggest[] = people
        .filter(
          (p) =>
            p.user.id !== meId && (p.name.toLowerCase().includes(q) || p.user.username.includes(q)),
        )
        .sort(
          (a, b) =>
            Number(presenceOf(state, a.user.id) === 'offline') -
            Number(presenceOf(state, b.user.id) === 'offline'),
        )
        .slice(0, 8)
        .map((p) => ({
          kind: 'user',
          id: p.user.id,
          insert: '@' + p.user.username + ' ',
          label: p.name,
          sub: p.user.username,
          user: p.user,
        }));
      for (const r of roles) {
        if (items.length >= 10) break;
        if (r.name.toLowerCase().includes(q))
          items.push({
            kind: 'role',
            id: r.id,
            insert: '@' + r.name + ' ',
            label: '@' + r.name,
            sub: t('ns.mention.role'),
          });
      }
      if (guildId && canEveryone)
        for (const k of ['everyone', 'here'])
          if (k.startsWith(q))
            items.push({
              kind: 'every',
              id: k,
              insert: '@' + k + ' ',
              label: '@' + k,
              sub: t(`hub.mention.${k}`),
            });
      return { kind: 'mention', start: caret - (m[2] ?? '').length - 1, items };
    }
    m = /(^|\s)#([^\s#]*)$/.exec(upto);
    if (m && guildId) {
      const q = (m[2] ?? '').toLowerCase();
      // in the sidebar's order
      const items: Suggest[] = channelsInSidebarOrder({ channels }, guildId)
        .filter(
          (c) => c.type === ChannelType.GUILD_TEXT && (c.name ?? '').toLowerCase().includes(q),
        )
        .slice(0, 8)
        .map((c) => ({
          kind: 'channel',
          id: c.id,
          insert: '#' + c.name + ' ',
          label: c.name ?? '',
          sub: c.parent_id ? (channels[c.parent_id]?.name ?? '') : '',
        }));
      return { kind: 'channel', start: caret - (m[2] ?? '').length - 1, items };
    }
    m = EMOJI_QUERY_RE.exec(upto);
    if (m) {
      const q = m[2] ?? '';
      const items: Suggest[] = suggestEmoji(q).map((s) => ({
        kind: 'emoji',
        id: s.token,
        insert: s.insert,
        label: `:${s.name}:`,
        sub: s.custom ? t('emo.suggest.server') : '',
        emoji: s.token,
      }));
      return { kind: 'emoji', start: caret - q.length - 1, items, q };
    }
    return null;
    // emojiLists and customOn: the server emoji suggestEmoji() reads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, caret, people, channels, guildId, meId, canEveryone, roles, t, emojiLists, customOn]);

  const open = !dismissed && !!suggest && suggest.items.length > 0;
  const cur = open ? Math.min(sel, suggest!.items.length - 1) : 0;

  const choose = (i: number) => {
    if (!suggest) return;
    const it = suggest.items[i];
    if (!it || suggest.start < 0) return;
    const before = text.slice(0, suggest.start);
    const after = text.slice(caret);
    const next = before + it.insert + after;
    setText(next);
    const pos = (before + it.insert).length;
    requestAnimationFrame(() => {
      ta.current?.setSelectionRange(pos, pos);
      setCaret(pos);
      ta.current?.focus();
      grow();
    });
  };

  /** A member menu's Mention (`insertIntoDraft`): into the draft at the caret, the field focused. */
  const insertText = (raw: string) => {
    const add = decodeMentions(
      raw,
      people.map((p) => p.user),
      Object.values(channels).filter((c) => c.guild_id === guildId),
      roles,
    );
    const el = ta.current;
    const draft = el?.value ?? text;
    const next = spliceDraft(draft, el ? el.selectionEnd : draft.length, add);
    setText(next.text);
    requestAnimationFrame(() => {
      ta.current?.focus();
      ta.current?.setSelectionRange(next.caret, next.caret);
      setCaret(next.caret);
      grow();
    });
  };
  const insertRef = useRef(insertText);
  useEffect(() => {
    insertRef.current = insertText;
  });
  useEffect(() => {
    if (!canSend) return;
    const take = () => {
      const add = takeDraftInsert(channel.id);
      if (add !== undefined) insertRef.current(add);
    };
    // what was asked before this composer opened, then whatever comes
    const raf = requestAnimationFrame(take);
    const off = useDraftInserts.subscribe(take);
    return () => {
      cancelAnimationFrame(raf);
      off();
    };
  }, [channel.id, canSend]);

  const patchPick = (key: number, patch: Partial<ImagePick>) =>
    setPicks((l) => l.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  const dropPick = (key: number) => {
    picks.find((p) => p.key === key)?.ctl.abort();
    setPicks((l) => l.filter((p) => p.key !== key));
  };

  // No size limit here: every image is optimized to fit the server's.
  const addFiles = (list: FileList | File[]) => {
    const incoming = Array.from(list);
    const images = incoming.filter(looksLikeImage);
    if (images.length < incoming.length)
      toast(t('ns.cmp.imagesOnly'), { icon: 'image', tone: 'warn' });
    const room = Math.max(0, maxFiles - picks.length);
    if (images.length > room)
      toast(t('img.tooMany', { n: maxFiles }), { icon: 'image', tone: 'warn' });
    const add = images.slice(0, room).map((source): ImagePick => {
      const ctl = new AbortController();
      const job: PickJob = { done: Promise.resolve(null), progress: 0, listeners: new Set(), ctl };
      return { key: ++pickSeq, source, state: 'working', progress: 0, out: null, ctl, job };
    });
    if (!add.length) return;
    setPicks((cur) => [...cur, ...add]);
    for (const p of add) {
      let shown = 0;
      const tell = (f: number) => {
        p.job.progress = f;
        for (const l of p.job.listeners) l();
      };
      p.job.done = optimizeImage(p.source, 'attachment', {
        uploadImageBytes,
        signal: p.ctl.signal,
        onProgress: (f) => {
          const pct = Math.floor(f * 20) * 5;
          if (pct === shown) return;
          shown = pct;
          patchPick(p.key, { progress: pct / 100 });
          tell(pct / 100);
        },
      }).then(
        (r) => {
          if (p.ctl.signal.aborted) return null;
          const out = new File([r.blob], renameFor(p.source.name || 'image', r.type), {
            type: r.type,
          });
          patchPick(p.key, { state: 'ready', progress: 1, out });
          tell(1);
          if (r.still) toast(stillText(t, r.still), { icon: 'image' });
          return out;
        },
        (e: unknown) => {
          tell(1);
          if (p.ctl.signal.aborted || isAbort(e)) return null;
          setPicks((l) => l.filter((x) => x.key !== p.key));
          toast(imageErrorText(t, e), { icon: 'image', tone: 'warn', duration: 7000 });
          return null;
        },
      );
    }
  };

  // Leaving the channel (or the page) stops whatever is still optimizing.
  const picksRef = useRef(picks);
  useEffect(() => {
    picksRef.current = picks;
  });
  useEffect(() => () => picksRef.current.forEach((p) => p.ctl.abort()), []);

  // Images dropped anywhere on the chat column come here.
  const addRef = useRef(addFiles);
  useEffect(() => {
    addRef.current = addFiles;
  });
  useEffect(() => {
    if (!canAttach) return;
    const host = wrap.current?.closest<HTMLElement>('.chat') ?? wrap.current;
    if (!host) return;
    let depth = 0;
    const hasFiles = (e: DragEvent) => !!e.dataTransfer?.types.includes('Files');
    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth += 1;
      setDropping(true);
    };
    const over = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const leave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDropping(false);
    };
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setDropping(false);
      if (e.dataTransfer?.files.length) addRef.current(e.dataTransfer.files);
    };
    host.addEventListener('dragenter', enter);
    host.addEventListener('dragover', over);
    host.addEventListener('dragleave', leave);
    host.addEventListener('drop', drop);
    return () => {
      host.removeEventListener('dragenter', enter);
      host.removeEventListener('dragover', over);
      host.removeEventListener('dragleave', leave);
      host.removeEventListener('drop', drop);
    };
  }, [canAttach, channel.id]);

  const optimizing = picks.some((p) => p.state === 'working');

  const send = () => {
    const raw = text.trim();
    const files = picks.flatMap((p) => (p.out ? [p.out] : []));
    if (!raw && !picks.length) return;
    // slowmode: the text stays until the countdown ends (the server would refuse it)
    if (slow.left > 0) {
      toast(t('sm.waitToast', { time: countdownText(slow.left) }), { icon: 'clock', tone: 'warn' });
      return;
    }
    const content = encodeCustomEmoji(
      encodeMentions(
        raw,
        people.map((p) => p.user),
        Object.values(channels).filter((c) => c.guild_id === guildId),
        roles,
      ),
      usableCustomEmoji(),
    );
    // the server counts the text it gets (mentions as <@id>) in characters, not UTF-16
    // units, up to the console's limit
    if (contentLength(content) > maxLength) {
      toast(t('ns.cmp.tooLong', { n: fmt.num(maxLength) }), { icon: 'alert', tone: 'warn' });
      return;
    }
    // Images still optimizing go with the message, which waits for them in
    // the channel (cancellable there); the composer is free again at once.
    const replyId = replyTo?.id ?? null;
    const pingReply = replyId && !replyMine ? ping : undefined;
    const preparing = optimizing ? preparedFiles(picks.map((p) => p.job)) : undefined;
    const go = () =>
      sendMessage(
        channel.id,
        content,
        preparing
          ? { replyTo: replyId, preparing, pingReply }
          : { replyTo: replyId, files, pingReply },
      );
    onSent?.();
    // sending while viewing older messages goes back to the present first (Discord), then sends,
    // so the new message lands in the history that is on screen
    if (useData.getState().messages[channel.id]?.hasNewer) void reloadHistory(channel.id).then(go);
    else go();
    noteSlowmodeSend(channel);
    setText('');
    setPicks([]);
    onCancelReply();
    requestAnimationFrame(grow);
  };

  /** ↑ in an empty composer edits your last message (I10). */
  const editLast = (): boolean => {
    const d = useData.getState();
    const cm = d.messages[channel.id];
    if (!cm || !meId) return false;
    for (let i = cm.ids.length - 1; i >= 0; i--) {
      const m = cm.byId[cm.ids[i]!];
      if (m && m.author.id === meId && (m.type === 0 || m.type === 19) && m.content) {
        startEdit(m.id);
        requestAnimationFrame(() =>
          document.getElementById(`msg-${m.id}`)?.scrollIntoView({ block: 'nearest' }),
        );
        return true;
      }
    }
    return false;
  };

  if (!canSend) {
    return (
      <div className="cmp-wrap" ref={wrap}>
        {topBar}
        <div className="cmp ns-cmp-locked" role="note">
          <Icon name="lock" size="s" />
          <span>{t('ns.cmp.noSend', { name })}</span>
        </div>
      </div>
    );
  }

  const ready = (!!text.trim() || picks.length > 0) && slow.left === 0;
  const popTitle = !suggest
    ? ''
    : suggest.kind === 'mention'
      ? t('hub.mention.title')
      : suggest.kind === 'emoji'
        ? t('emo.suggest.title', { q: suggest.q ?? '' })
        : t('hub.chpop.title');
  return (
    <div className="cmp-wrap" data-cmp-host="" ref={wrap}>
      {polling ? <PollCreator channelId={channel.id} onClose={() => setPolling(false)} /> : null}
      {open ? (
        <div className="cpop ns-cpop nx-raised" role="listbox" aria-label={popTitle}>
          <div className="cpop-h">{popTitle}</div>
          <div className="cpop-list nx-scroll">
            {suggest!.items.map((it, i) => (
              <div
                key={it.kind + it.id}
                className="cpop-row"
                role="option"
                id={`cpop-${i}`}
                aria-selected={i === cur}
                onPointerDown={(e) => e.preventDefault()}
                onPointerEnter={() => setSel(i)}
                onClick={() => choose(i)}
              >
                {it.user ? (
                  <Avatar user={it.user} size={24} />
                ) : it.emoji ? (
                  <span className="cpop-ic cpop-emo">
                    <Emoji e={it.emoji} />
                  </span>
                ) : (
                  <span className="cpop-ic">
                    <Icon
                      name={it.kind === 'channel' ? 'hash' : it.kind === 'role' ? 'shield' : 'at'}
                      size="s"
                    />
                  </span>
                )}
                <span className="cpop-t">
                  <b dir={it.kind === 'emoji' ? 'ltr' : 'auto'}>{it.label}</b>
                  {it.sub ? (
                    <small dir={it.kind === 'user' ? 'ltr' : 'auto'}>{it.sub}</small>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {topBar}
      {slow.rate > 0 && !slow.exempt ? <SlowmodeBar channel={channel} left={slow.left} /> : null}
      {replyTo ? (
        <div className="cmp-bar cmp-reply">
          <span className="cr-t">
            {/* "Replying to {name}": the UI's sentence, the name isolated in it */}
            <span>
              {t('ns.cmp.replying')}{' '}
              <strong>
                <bdi>{replyName}</bdi>
              </strong>
            </span>
          </span>
          {!replyMine ? (
            <button
              type="button"
              className="cr-m"
              aria-pressed={ping}
              aria-label={t(ping ? 'chat.reply.pingOn' : 'chat.reply.pingOff')}
              data-tip={t(ping ? 'chat.reply.pingOn' : 'chat.reply.pingOff')}
              onClick={() => setPing((v) => !v)}
            >
              @ {t(ping ? 'chat.reply.on' : 'chat.reply.off')}
            </button>
          ) : null}
          <button
            type="button"
            className="cr-x"
            aria-label={t('hub.cmp.cancel')}
            onClick={onCancelReply}
          >
            <Icon name="x" size="s" />
          </button>
        </div>
      ) : null}
      <div className="cmp" data-drop={dropping || undefined}>
        {dropping ? (
          <div className="ns-drop-hint" aria-hidden="true">
            <Icon name="upload" size="s" />
            {t('img.drop')}
          </div>
        ) : null}
        {picks.length ? (
          <div className="ns-files">
            {picks.map((p) => (
              <FileChip key={p.key} pick={p} onRemove={() => dropPick(p.key)} />
            ))}
          </div>
        ) : null}
        <div className="cmp-row">
          {canAttach || canPoll ? (
            <>
              <button
                type="button"
                className="cmp-btn"
                aria-label={t(canPoll ? 'pl.plus' : 'ns.cmp.attach')}
                data-tip={t(canPoll ? 'pl.plus' : 'ns.cmp.attach')}
                aria-haspopup={canPoll ? 'menu' : undefined}
                onClick={(e) =>
                  // with polls the "+" is Discord's menu: upload, create a poll
                  canPoll
                    ? openMenu(
                        e.currentTarget,
                        [
                          ...(canAttach
                            ? [
                                {
                                  label: t('pl.upload'),
                                  icon: 'upload' as const,
                                  run: () => fileIn.current?.click(),
                                },
                              ]
                            : []),
                          {
                            label: t('pl.create'),
                            icon: 'poll' as const,
                            run: () => setPolling(true),
                          },
                        ],
                        { placement: 'top-start' },
                      )
                    : fileIn.current?.click()
                }
              >
                <Icon name="plus-circle" size="m" />
              </button>
              {canAttach ? (
                <input
                  ref={fileIn}
                  type="file"
                  accept={IMAGE_ACCEPT}
                  multiple
                  hidden
                  onChange={(e) => {
                    if (e.target.files) addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              ) : null}
            </>
          ) : null}
          <div className="cmp-field">
            <textarea
              ref={ta}
              rows={1}
              className="cmp-in"
              dir="auto"
              value={text}
              placeholder={placeholder}
              aria-label={placeholder}
              aria-autocomplete="list"
              aria-activedescendant={open ? `cpop-${cur}` : undefined}
              enterKeyHint={touch ? 'enter' : undefined}
              spellCheck={false}
              onChange={(e) => {
                setText(e.target.value);
                setCaret(e.target.selectionStart);
                setSel(0);
                setDismissed(false);
                grow();
                if (e.target.value.trim()) sendTyping(channel.id);
              }}
              onSelect={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart)}
              onPaste={(e) => {
                if (!canAttach) return;
                const imgs = Array.from(e.clipboardData.files).filter(looksLikeImage);
                if (imgs.length) {
                  e.preventDefault();
                  addFiles(imgs);
                }
              }}
              onKeyDown={(e) => {
                if (open) {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSel((cur + 1) % suggest!.items.length);
                    return;
                  }
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSel((cur - 1 + suggest!.items.length) % suggest!.items.length);
                    return;
                  }
                  if (e.key === 'Enter' || e.key === 'Tab') {
                    e.preventDefault();
                    choose(cur);
                    return;
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    setDismissed(true);
                    return;
                  }
                }
                if (e.key === 'Escape' && replyTo) {
                  e.preventDefault();
                  e.stopPropagation();
                  onCancelReply();
                  return;
                }
                if (
                  e.key === 'ArrowUp' &&
                  !text &&
                  !picks.length &&
                  !e.shiftKey &&
                  !e.altKey &&
                  !e.ctrlKey &&
                  !e.metaKey &&
                  editLast()
                ) {
                  e.preventDefault();
                  return;
                }
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && !touch) {
                  e.preventDefault();
                  send();
                }
              }}
            />
          </div>
          <div className="cmp-tools">
            <button
              type="button"
              className="cmp-btn emoji"
              aria-label={t('hub.cmp.emoji')}
              data-tip={t('hub.cmp.emoji')}
              onClick={(e) =>
                openEmoji(e.currentTarget, (picked) => {
                  // a server emoji is written :name: (sent as <:name:id>, as on Discord)
                  const em = isCustomToken(picked) ? `:${customName(picked) ?? ''}:` : picked;
                  // the field as it is now (the picker stays open for Shift-click: never a stale copy)
                  const el = ta.current;
                  const cur = el ? el.value : text;
                  const start = el ? el.selectionStart : cur.length;
                  const end = el ? el.selectionEnd : start;
                  const next = cur.slice(0, start) + em + cur.slice(end);
                  const pos = start + em.length;
                  if (el) {
                    el.value = next;
                    el.setSelectionRange(pos, pos);
                  }
                  setText(next);
                  setCaret(pos);
                  requestAnimationFrame(() => {
                    el?.focus();
                    grow();
                  });
                })
              }
            >
              <Icon name="smile" size="m" />
            </button>
            <button
              type="button"
              className={'cmp-send' + (ready ? ' is-ready' : '')}
              aria-label={t('hub.cmp.send')}
              data-tip={t('hub.cmp.send')}
              onClick={send}
            >
              <Icon name="send" size="m" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * An image waiting to be sent: its thumbnail (the optimized image, small,
 * never the original, maybe 100 MB), then its progress while optimizing.
 * The thumbnail's URL is made and revoked in one effect: made in a memo and
 * revoked in an effect's cleanup, StrictMode's second effect run
 * (development) revoked the URL the <img> still showed, so it broke. Here
 * each run makes its own URL (set before paint, so the chip never shows an
 * empty frame).
 */
export function FileChip({ pick, onRemove }: { pick: ImagePick; onRemove(): void }) {
  const { t } = useT();
  const img = useRef<HTMLImageElement>(null);
  useLayoutEffect(() => {
    if (!pick.out) return;
    const url = URL.createObjectURL(pick.out);
    if (img.current) img.current.src = url;
    return () => URL.revokeObjectURL(url);
  }, [pick.out]);
  const name = pick.source.name || 'image';
  const pct = Math.round(pick.progress * 100);
  // The final size, only as a tooltip when it is much smaller than what was picked.
  const title =
    pick.out && pick.out.size < pick.source.size * 0.8
      ? t('img.optimizedTitle', {
          from: sizeText(t, pick.source.size),
          to: sizeText(t, pick.out.size),
        })
      : undefined;
  return (
    <div className="ns-file-chip" data-state={pick.state} title={title}>
      {pick.out ? (
        <img ref={img} alt="" />
      ) : (
        <span className="ns-chip-wait" aria-hidden="true">
          <i className="nx-spin" />
        </span>
      )}
      <span className="ns-chip-t">
        <span dir="ltr">{name}</span>
        {pick.state === 'working' ? (
          <small role="status">
            {t('img.optimizing', { n: pct })}
            <span className="ns-prog" aria-hidden="true">
              <i style={{ width: `${pct}%` }} />
            </span>
          </small>
        ) : null}
      </span>
      <button
        type="button"
        aria-label={
          pick.state === 'working'
            ? t('img.cancelOptimize', { name })
            : t('ns.cmp.remove', { name })
        }
        onClick={onRemove}
      >
        <Icon name="x" size="s" />
      </button>
    </div>
  );
}
